import React, { useState, useMemo, useCallback } from 'react';
import { compose } from 'redux';
import { injectIntl, FormattedMessage } from '../../util/reactIntl';
import config from '../../config';
import { skillOrToolLabel } from '../../util/skillAndToolLabels';
import ToolLogo from '../ToolLogo/ToolLogo';

import css from './FreelancerAboutMeContainer.css';

// Constants
const BIO_PREVIEW_LENGTH = 600;
const INITIAL_ITEMS_COUNT = 5;
const HTML_ENTITY_MAX_LENGTH = 10;

// Helper functions
const getSafeBioSlice = (text, maxLength) => {
  if (text.length <= maxLength) return text;

  // For HTML content, we need to be more careful about truncation
  // First, let's try to get a rough estimate by removing HTML tags to measure text length
  const textOnly = text.replace(/<[^>]*>/g, '');

  // If the text content without HTML is within limit, return original
  if (textOnly.length <= maxLength) return text;

  // If we need to truncate, we'll work with the original HTML
  let sliced = text.slice(0, maxLength);

  // Handle HTML entities (like &nbsp;, &amp;, etc.)
  const lastAmpersand = sliced.lastIndexOf('&');
  const lastSemicolon = sliced.lastIndexOf(';');

  if (lastAmpersand > lastSemicolon) {
    const nextSemicolon = text.indexOf(';', maxLength);
    if (nextSemicolon !== -1 && nextSemicolon - lastAmpersand < HTML_ENTITY_MAX_LENGTH) {
      sliced = text.slice(0, lastAmpersand);
    }
  }

  // Handle broken HTML tags by removing incomplete tags at the end
  const lastOpenTag = sliced.lastIndexOf('<');
  const lastCloseTag = sliced.lastIndexOf('>');

  // If we have an unclosed tag at the end, remove it
  if (lastOpenTag > lastCloseTag) {
    sliced = sliced.slice(0, lastOpenTag);
  }

  // Clean up any trailing incomplete words
  const lastSpace = sliced.lastIndexOf(' ');
  if (lastSpace > sliced.length * 0.8) {
    sliced = sliced.slice(0, lastSpace);
  }

  return sliced;
};

// Custom hooks
const useProfileData = profileUser => {
  return useMemo(() => {
    const publicData = profileUser?.attributes?.profile?.publicData || {};
    const rawBio = publicData.bio || '';

    const skillKeys =
      Array.isArray(publicData.experienceKeys) && publicData.experienceKeys.length
        ? publicData.experienceKeys
        : (publicData.experience || []).map(e => e?.category).filter(Boolean);

    return {
      bio: rawBio, // Keep HTML formatting completely intact
      skills: skillKeys.map(category => ({ category })),
      tools: publicData.tools || [],
      hobbies: publicData.hobbies || [],
    };
  }, [profileUser]);
};

const useTranslationHelpers = intl => {
  const locale = config.locale;
  const translationKey = locale === 'fi' ? 'fiTranslation' : 'enTranslation';
  const hobbyOptions = config.custom.hobbyOptions;

  const messages = useMemo(
    () => ({
      header: intl.formatMessage({ id: 'FreelancerAboutMeContainer.header' }),
      skills: intl.formatMessage({ id: 'FreelancerAboutMeContainer.skills' }),
      tools: intl.formatMessage({ id: 'FreelancerAboutMeContainer.tools' }),
      hobbies: intl.formatMessage({ id: 'FreelancerAboutMeContainer.hobbies' }),
      showMore: intl.formatMessage({ id: 'FreelancerAboutMeContainer.showMore' }),
      showLess: intl.formatMessage({ id: 'FreelancerAboutMeContainer.showLess' }),
    }),
    [intl]
  );

  return { translationKey, hobbyOptions, messages };
};

// Memoized components
const BioSection = React.memo(({ bio, showFullBio, toggleBio, messages }) => {
  const bioData = useMemo(() => {
    const isBioLong = bio.length > BIO_PREVIEW_LENGTH;
    const displayedBio =
      showFullBio || !isBioLong ? bio : `${getSafeBioSlice(bio, BIO_PREVIEW_LENGTH)}...`;

    return { isBioLong, displayedBio };
  }, [bio, showFullBio]);

  return (
    <div id="bio" className={css.bio}>
      <div className={css.bioContent} dangerouslySetInnerHTML={{ __html: bioData.displayedBio }} />
      {bioData.isBioLong && (
        <div className={css.toggleShowMore} onClick={toggleBio}>
          {showFullBio ? messages.showLess : messages.showMore}
        </div>
      )}
    </div>
  );
});

// Shared pill row used for both the Skills and the Tools sections.
// Items are { key, label, logoUrl? } — a logo is rendered only when the item
// has a logoUrl (skills never do).
const PillSection = React.memo(({ id, heading, items, showFull, onShowMore, pillClassName }) => {
  const sectionData = useMemo(() => {
    const initialItems = items.slice(0, INITIAL_ITEMS_COUNT);
    const remainingItems = items.slice(INITIAL_ITEMS_COUNT);

    return {
      displayedItems: showFull ? items : initialItems,
      hasMoreItems: remainingItems.length > 0,
      remainingCount: remainingItems.length,
    };
  }, [items, showFull]);

  if (!items || items.length === 0) return null;

  return (
    <div id={id} className={css.sectionSkillsAndTools}>
      <div className={css.skillsAndToolsHeading}>{heading}</div>
      <div className={css.skillsAndTools}>
        {sectionData.displayedItems.map(({ key, label, logoUrl }) => (
          <div key={key} className={pillClassName || css.skillContainer}>
            <ToolLogo logoUrl={logoUrl} className={css.toolLogo} />
            <div className={css.heading}>{label}</div>
          </div>
        ))}
        {sectionData.hasMoreItems && !showFull && (
          <div className={css.toggleShowMoreSkills} onClick={onShowMore}>
            <FormattedMessage
              id="FreelancerAboutMeContainer.showMoreSkills"
              values={{ count: sectionData.remainingCount }}
            />
          </div>
        )}
      </div>
    </div>
  );
});

const HobbiesSection = React.memo(
  ({ hobbies, showFullHobbies, toggleHobbies, hobbyOptions, messages }) => {
    const hobbiesData = useMemo(() => {
      // Sort hobbies alphabetically by translated label
      const sortedHobbies = [...hobbies].sort((a, b) => {
        const labelA = hobbyOptions.find(h => h.key === a.category)?.label;
        const labelB = hobbyOptions.find(h => h.key === b.category)?.label;
        const textA = typeof labelA === 'string' ? labelA : labelA?.props?.defaultMessage || '';
        const textB = typeof labelB === 'string' ? labelB : labelB?.props?.defaultMessage || '';
        return textA.localeCompare(textB);
      });

      const initialHobbies = sortedHobbies.slice(0, INITIAL_ITEMS_COUNT);
      const remainingHobbies = sortedHobbies.slice(INITIAL_ITEMS_COUNT);
      const hasMoreHobbies = remainingHobbies.length > 0;
      const displayedHobbies = showFullHobbies ? sortedHobbies : initialHobbies;

      return {
        displayedHobbies,
        hasMoreHobbies,
        remainingCount: remainingHobbies.length,
      };
    }, [hobbies, showFullHobbies, hobbyOptions]);

    if (!hobbies || hobbies.length === 0) return null;

    return (
      <div id="hobbies" className={css.sectionHobbies}>
        <div className={css.hobbiesHeading}>{messages.hobbies}</div>
        <div className={css.hobbies}>
          {hobbiesData.displayedHobbies.map(({ category }) => (
            <div key={category} className={css.hobbyContainer}>
              <div className={css.heading}>{hobbyOptions.find(h => h.key === category)?.label}</div>
            </div>
          ))}
          {hobbiesData.hasMoreHobbies && !showFullHobbies && (
            <div className={css.toggleShowMoreSkills} onClick={toggleHobbies}>
              <FormattedMessage
                id="FreelancerAboutMeContainer.showMoreSkills"
                values={{ count: hobbiesData.remainingCount }}
              />
            </div>
          )}
        </div>
      </div>
    );
  }
);

const FreelancerAboutMeContainerComponent = ({ profileUser, skillOptions, toolOptions, intl }) => {
  const [showFullBio, setShowFullBio] = useState(false);
  const [showFullSkills, setShowFullSkills] = useState(false);
  const [showFullTools, setShowFullTools] = useState(false);
  const [showFullHobbies, setShowFullHobbies] = useState(false);

  const { bio, skills, tools, hobbies } = useProfileData(profileUser);
  const { translationKey, hobbyOptions, messages } = useTranslationHelpers(intl);

  // Items without a label yet (lists still loading) are left out, so the
  // sections never show raw keys.
  const skillItems = useMemo(
    () =>
      skills
        .map(({ category }) => ({
          key: category,
          label: skillOrToolLabel(category, [skillOptions], translationKey),
        }))
        .filter(item => item.label),
    [skills, skillOptions, translationKey]
  );

  const toolItems = useMemo(
    () =>
      tools
        .map(({ category }) => ({
          key: category,
          label: skillOrToolLabel(category, [toolOptions], translationKey),
          logoUrl: toolOptions.find(t => t.key === category)?.logoUrl,
        }))
        .filter(item => item.label),
    [tools, toolOptions, translationKey]
  );

  // Event handlers
  const toggleBio = useCallback(() => setShowFullBio(prev => !prev), []);
  const toggleSkills = useCallback(() => setShowFullSkills(true), []);
  const toggleTools = useCallback(() => setShowFullTools(true), []);
  const toggleHobbies = useCallback(() => setShowFullHobbies(prev => !prev), []);

  return (
    <div className={css.aboutMeContainer}>
      <div className={css.sectionTitle}>{messages.header}</div>

      <BioSection bio={bio} showFullBio={showFullBio} toggleBio={toggleBio} messages={messages} />

      <PillSection
        id="experience"
        heading={messages.skills}
        items={skillItems}
        showFull={showFullSkills}
        onShowMore={toggleSkills}
      />

      <PillSection
        id="tools"
        heading={messages.tools}
        items={toolItems}
        showFull={showFullTools}
        onShowMore={toggleTools}
        pillClassName={css.toolContainer}
      />

      <HobbiesSection
        hobbies={hobbies}
        showFullHobbies={showFullHobbies}
        toggleHobbies={toggleHobbies}
        hobbyOptions={hobbyOptions}
        messages={messages}
      />
    </div>
  );
};

const FreelancerAboutMeContainer = compose(injectIntl)(FreelancerAboutMeContainerComponent);

export default FreelancerAboutMeContainer;
